// A COMPARISON ANSWER (busy-ai-srv#41, Figma "⚖️ Compare" in the final search design 40015125:58088).
//
// "airpods pro 3 vs bose quietcomfort ultra earbuds", "python or javascript for a beginner", "w
// barcelona vs hotel arts": ai-srv's planner reads the question as a comparison of 2–4 named things
// and sends a `compare` block — `{ kind, options: [{ name, bestFor, image, thumb, price, store,
// storeCount, url, buyable }] }` — on its own SSE event (as soon as the per-option prices and
// pictures are in) and again on `done` / the one-shot body. The page draws it as the OPTION COLUMNS
// under the verdict (components/SmartBox/CompareOptions.vue), and the "Choose X if…" lines the
// answer ends with as the side-by-side CHOOSE CARDS (CompareChooseCards.vue). The table's
// `[[better]]` marks are lib/compareMarkers.js.
//
// Pure: no store, no DOM. Nothing here changes an answer that is not a comparison — `toCompare` is
// null, `chooseRuns` hands every item back on its own, `chooseSegments` hands the text back whole.

import { answerUnitSpans } from '@/lib/answerUnits'

export const COMPARE_KINDS = ['products', 'services', 'places', 'ideas', 'medicine']
// The kinds with nothing to buy or book: never a price, never "Track". Medicine has no picture
// either — the spec's "NO winner, NO prices, NO pictures".
const UNPRICED = new Set(['ideas', 'medicine'])
const UNPICTURED = new Set(['medicine'])
const MAX_OPTIONS = 4

const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v)
const str = (v) => (typeof v === 'string' ? v.trim() : '')
// A picture or a seller page: web addresses only, and a picture over https (an http one is blocked
// as mixed content on the app, exactly as the photo row's are upgraded in the research store).
const webUrl = (v) => (/^https?:\/\//i.test(str(v)) ? str(v) : '')
const picture = (v) => webUrl(v).replace(/^http:\/\//i, 'https://')

/**
 * One option off the wire → what a column draws, or null when it has no name. Read under both
 * casings the wire may use (the BFF's one-shot body is PascalCase-tolerant like the widget's).
 */
function toOption(raw, kind) {
  if (!isObj(raw)) return null
  const name = str(raw.name ?? raw.Name)
  if (!name) return null
  const pictured = !UNPICTURED.has(kind)
  const priced = !UNPRICED.has(kind)
  const count = Number(raw.storeCount ?? raw.StoreCount)
  const price = priced ? str(raw.price ?? raw.Price) : ''
  return {
    name,
    bestFor: str(raw.bestFor ?? raw.BestFor),
    image: pictured ? picture(raw.image ?? raw.Image) : '',
    thumb: pictured ? picture(raw.thumb ?? raw.Thumb) : '',
    price,
    store: price ? str(raw.store ?? raw.Store) : '',
    storeCount: price && Number.isInteger(count) && count > 0 ? count : 0,
    url: priced ? webUrl(raw.url ?? raw.Url) : '',
    buyable: priced && (raw.buyable ?? raw.Buyable) === true,
  }
}

/**
 * The wire's `compare` → `{ kind, options }`, or null when there is no comparison to draw: absent,
 * an unknown kind, or fewer than two named options (the spec: "When `compare` is present with ≥2
 * options"). At most four options. The `compare` SSE event may carry the block itself or wrap it as
 * `{ compare }` — both read.
 *
 * @param {object|null|undefined} raw
 * @returns {{ kind: string, options: object[] } | null}
 */
export function toCompare(raw) {
  const block = isObj(raw?.compare ?? raw?.Compare) ? (raw.compare ?? raw.Compare) : raw
  if (!isObj(block)) return null
  const kind = str(block.kind ?? block.Kind).toLowerCase()
  if (!COMPARE_KINDS.includes(kind)) return null
  const list = block.options ?? block.Options
  const options = (Array.isArray(list) ? list : []).map(o => toOption(o, kind)).filter(Boolean).slice(0, MAX_OPTIONS)
  return options.length >= 2 ? { kind, options } : null
}

/** Does a turn carry a comparison to draw? (`turn.compare` is only ever `toCompare`'s output.) */
export const hasCompare = (turn) => (turn?.compare?.options?.length || 0) >= 2

/** Does this option get the price row and its two buttons? */
export const optionHasPrice = (o) => !!str(o?.price)

/** The option's picture: the engine's small thumbnail when there is one, else the picture. */
export const optionPicture = (o) => o?.thumb || o?.image || ''

// ── "Choose X if…" → the Choose cards (Figma 40015163:56028) ─────────────────────────────────────
//
// The answer ends its comparison with a heading and, per option, a bold line followed by 2–4 bullets:
// `**Choose AirPods Pro 3 if…**` / `**Upgrade if…**` / `**Keep your 17 Pro Max if…**` / `**Stay at
// Hotel Arts if…**`. Each such pair is a card; consecutive pairs are one side-by-side row. A bold
// "…if…" line with NO list under it is left as the paragraph it was.

// The bold line, alone on its line: `**…if…**` — the ellipsis inside or just outside the bold, as
// "…" or "...", a trailing colon tolerated ("**Upgrade if:**").
const CHOOSE = /^\*\*\s*(.+?\bif)\s*(?:…|\.{3})?\s*:?\s*\*\*\s*(?:…|\.{3})?\s*:?$/i
const BULLET = /^\s*[-*•]\s+/

/**
 * A line → the card's title ("Choose AirPods Pro 3 if…"), or '' when it is not a Choose line.
 *
 * @param {string} md one line of the answer
 * @returns {string}
 */
export function chooseTitle(md) {
  const m = String(md ?? '').trim().match(CHOOSE)
  if (!m || m[1].includes('**')) return ''
  return `${m[1].trim()}…`
}

/** Is this unit a bullet line? */
export const isBulletMd = (md) => BULLET.test(String(md ?? ''))

/** A bullet line's words, without its bullet — what one reason in a card reads. */
export const bulletBody = (md) => String(md ?? '').replace(BULLET, '').trim()

/**
 * A list of answer items (finished chunks, or any objects carrying markdown) → RUNS, in order:
 * `{ kind: 'item', item }` for an item drawn as it always was, and `{ kind: 'choose', cards }` for a
 * row of consecutive Choose cards, each card `{ title, head, reasons }` (`head` the bold line's item,
 * `reasons` its bullets' items). No Choose line → every item is its own run, unchanged.
 *
 * @param {Array} items
 * @param {(item: any) => string} [mdOf] the item's markdown (the item itself by default)
 * @returns {Array<{kind: 'item', item: any} | {kind: 'choose', cards: object[]}>}
 */
export function chooseRuns(items, mdOf = (x) => x) {
  const list = Array.isArray(items) ? items : []
  const runs = []
  let i = 0
  while (i < list.length) {
    const title = chooseTitle(mdOf(list[i]))
    let j = i + 1
    while (title && j < list.length && isBulletMd(mdOf(list[j]))) j++
    if (!title || j === i + 1) {
      runs.push({ kind: 'item', item: list[i] })
      i++
      continue
    }
    const card = { title, head: list[i], reasons: list.slice(i + 1, j) }
    const last = runs[runs.length - 1]
    if (last?.kind === 'choose') last.cards.push(card)
    else runs.push({ kind: 'choose', cards: [card] })
    i = j
  }
  return runs
}

/**
 * The STREAMING (or restored) answer text cut the same way: `{ kind: 'md', md }` slices of the text
 * as received, and `{ kind: 'choose', cards }` rows with each card `{ title, reasons: [md] }`. Text
 * without a Choose card comes back as ONE slice — the whole text, untouched — so an answer that is
 * not a comparison renders through exactly the markup it always did.
 *
 * @param {string} md
 * @returns {Array<{kind: 'md', md: string} | {kind: 'choose', cards: object[]}>}
 */
export function chooseSegments(md) {
  const src = String(md ?? '')
  const spans = answerUnitSpans(src)
  const runs = chooseRuns(spans, s => (s.kind === 'line' ? s.md : ''))
  if (!runs.some(r => r.kind === 'choose')) return [{ kind: 'md', md: src }]
  const out = []
  let from = 0
  for (const r of runs) {
    if (r.kind !== 'choose') continue
    const start = r.cards[0].head.start
    const before = src.slice(from, start)
    if (before.trim()) out.push({ kind: 'md', md: before })
    out.push({ kind: 'choose', cards: r.cards.map(c => ({ title: c.title, reasons: c.reasons.map(s => s.md) })) })
    const lastCard = r.cards[r.cards.length - 1]
    from = lastCard.reasons[lastCard.reasons.length - 1].end
  }
  const after = src.slice(from)
  if (after.trim()) out.push({ kind: 'md', md: after })
  return out
}

// ── An option's prices → the existing "Best prices" panel ────────────────────────────────────────
//
// An option's "See all prices" opens the SAME panel the single best-price strip opens, on the product
// group (lib/bestPrice.js `groupOffers`) that IS this option. The groups are named by the stores'
// titles, so the match is by words: every number of the option's name must be there (an "iPhone 17
// Pro Max" is not an "iPhone 18 Pro Max"), and most of its other words.

const wordsOf = (s) => String(s ?? '').toLowerCase()
  .normalize('NFKD').replace(/[̀-ͯ]/g, '')
  .match(/[\p{L}\p{N}]+/gu) || []

/**
 * The product group an option's prices are in, or null when none is close enough.
 *
 * @param {Array<{name: string}>} groups the turn's product groups
 * @param {{name: string}} option
 * @returns {object|null}
 */
export function groupForOption(groups, option) {
  const want = wordsOf(option?.name).filter(w => w.length > 1 || /\d/.test(w))
  if (!want.length) return null
  const numbers = want.filter(w => /\d/.test(w))
  let best = null
  let bestScore = 0
  for (const g of Array.isArray(groups) ? groups : []) {
    const have = new Set(wordsOf(g?.name))
    if (!numbers.every(n => have.has(n))) continue
    const score = want.filter(w => have.has(w)).length / want.length
    if (score > bestScore) { best = g; bestScore = score }
  }
  return bestScore >= 0.6 ? best : null
}
